import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation, useRTL } from '../utils/translations';
import { assetSrc, uimetadata } from '../utils/metadata';

// A single exterior/interior colour combination the SDK considers compatible
// with whatever accessory/package the user is trying to apply.
interface ColorOption {
  exterior: string;
  interior: string;
}

interface OptionsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (option: ColorOption) => void;
  options: ColorOption[];
  modelId: string;
  currentExteriorColorID?: string;
  currentInteriorColorID?: string;
  isDarkMode: boolean;
  /** Name of the accessory/package the user was trying to apply, for the description. */
  itemLabel?: string;
}

/**
 * Shown whenever the SDK's colour-conflict check finds that the accessory or
 * package the user clicked is only compatible with a DIFFERENT exterior/interior
 * colour than the one currently selected (GenerateOptions/PauseExecutionForOptions
 * in the SDK). Until this existed, that promise was never resolved by anything in
 * the app, so the click just hung forever with no feedback - see e.g. Tahoe's
 * Front Storage Box (bonnet) or Silverado's Midnight Edition package, which is
 * compatible with only 1 of its variant's 9 exterior colours.
 *
 * Resolving with a chosen option lets the SDK switch to that colour combination
 * and then continue applying the accessory/package normally; closing without a
 * choice (resolveOptions(null) - a no-op is functionally identical to rejecting,
 * since the SDK's own onClick treats a falsy resolution and a rejection the same
 * way: `if (!selectedOption) return`) leaves the current colour and configuration
 * untouched.
 */
export const OptionsDialog: React.FC<OptionsDialogProps> = ({
  isOpen,
  onClose,
  onSelect,
  options,
  modelId,
  currentExteriorColorID,
  currentInteriorColorID,
  isDarkMode,
  itemLabel,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useRTL();

  const extColors: any = (uimetadata.models as any)?.[modelId]?.colors?.exterior ?? {};
  const intColors: any = (uimetadata.models as any)?.[modelId]?.colors?.interior ?? {};

  // Only show the interior label/swatch when at least one option actually offers
  // a different interior than what's currently selected - most cases here only
  // ever vary the exterior, and repeating an unchanged interior on every card
  // would just be noise.
  const interiorVaries = options.some((o) => o.interior && o.interior !== currentInteriorColorID);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/[0.88] z-[100]"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            dir={isRTL ? 'rtl' : 'ltr'}
            className={cn(
              'fixed left-[4%] right-[4%] md:left-1/2 md:right-auto top-1/2 -translate-y-1/2 md:-translate-x-1/2 w-[92%] md:w-full md:max-w-[680px] max-h-[90dvh] rounded-[var(--brand-dialog-radius)] shadow-2xl z-[101] overflow-hidden flex flex-col',
              isDarkMode ? 'bg-[#111111]' : 'bg-white',
            )}
          >
            {/* Header */}
            <div className={cn('relative px-[24px] md:px-[47px] pb-0', isDarkMode ? 'bg-[#111111]' : 'bg-white')}>
              <button
                onClick={onClose}
                className={cn(
                  'absolute top-[20px] w-[34px] h-[34px] rounded-full flex items-center justify-center transition-colors',
                  isRTL ? 'left-[20px] md:left-[22px]' : 'right-[20px] md:right-[22px]',
                  isDarkMode
                    ? 'border border-white/20 text-white hover:bg-white/10'
                    : 'border border-[#e2e2e2] text-[#282828] hover:bg-gray-100',
                )}
              >
                <X className="w-[14px] h-[14px]" />
              </button>

              <h2 className={cn(
                'text-[18px] md:text-[24px] font-semibold mt-[47px] mb-[8px]',
                isRTL ? 'pl-10' : 'pr-10',
                isDarkMode ? 'text-white' : 'text-[#282828]',
              )}>
                {t('staticTranslations.optionsDialog.title', 'Choose a Compatible Colour')}
              </h2>
              <p className={cn('text-[12px] md:text-[16px] leading-normal pb-[16px]', isDarkMode ? 'text-white/60' : 'text-[#333334]')}>
                {itemLabel
                  ? t('staticTranslations.optionsDialog.descriptionWithItem', `${itemLabel} isn't available on the current colour. Select one of the compatible options below to continue.`)
                  : t('staticTranslations.optionsDialog.description', "This isn't available on the current colour. Select one of the compatible options below to continue.")}
              </p>
            </div>

            {/* Option list */}
            <div className={cn('px-[24px] md:px-[47px] py-[16px] overflow-y-auto flex-1 min-h-0', isDarkMode ? 'bg-[#111111]' : 'bg-white')}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-[12px]">
                {options.map((option, idx) => {
                  const ext = extColors[option.exterior];
                  const intColor = intColors[option.interior];
                  return (
                    <button
                      key={`${option.exterior}-${option.interior}-${idx}`}
                      onClick={() => onSelect(option)}
                      className={cn(
                        'flex flex-col items-stretch overflow-hidden rounded-[var(--brand-card-radius)] border-[1.2px] text-left transition-colors',
                        isDarkMode ? 'bg-white/5 border-white/10 hover:border-white/30' : 'bg-white border-[#e6e6e6] hover:border-[var(--brand-accent)]',
                      )}
                    >
                      <div className="h-[80px] w-full overflow-hidden bg-gray-100 relative flex-shrink-0">
                        {ext?.image ? (
                          <img src={assetSrc(ext.image)} alt={ext?.label?.en ?? option.exterior} className="w-full h-full object-cover" />
                        ) : ext?.hex ? (
                          <div className="w-full h-full" style={{ backgroundColor: ext.hex }} />
                        ) : (
                          <div className={cn('w-full h-full', isDarkMode ? 'bg-white/10' : 'bg-gray-200')} />
                        )}
                      </div>
                      <div className="px-[10px] py-[8px] flex flex-col gap-[2px] min-w-0">
                        <span className={cn('text-[12px] font-bold truncate', isDarkMode ? 'text-white' : 'text-[#212121]')}>
                          {isRTL ? (ext?.label?.ar ?? ext?.label?.en ?? option.exterior) : (ext?.label?.en ?? option.exterior)}
                        </span>
                        {interiorVaries && (
                          <span className={cn('text-[10px] truncate', isDarkMode ? 'text-white/50' : 'text-[#666]')}>
                            {isRTL ? (intColor?.label?.ar ?? intColor?.label?.en ?? option.interior) : (intColor?.label?.en ?? option.interior)}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className={cn(
              'flex items-center justify-end gap-[12px] px-[24px] md:px-[47px] h-[77px] md:h-[83px] flex-shrink-0',
              isDarkMode ? 'bg-[#1a1a1a]' : 'bg-[#eaeaea]',
            )}>
              <button
                onClick={onClose}
                className={cn(
                  'h-[47px] px-[36px] min-w-[160px] rounded-[var(--brand-button-radius-sm)] text-[13px] md:text-[16px] font-semibold border transition-colors',
                  isDarkMode
                    ? 'border-white/30 text-white bg-transparent hover:bg-white/10'
                    : 'border-[var(--brand-accent)] text-[var(--brand-accent)] bg-[#eaeaea] hover:bg-blue-50',
                )}
              >
                {t('staticTranslations.common.cancel', 'Cancel')}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
